/**
 * avwap — Anchored Volume-Weighted Average Price (Wave D, v7.5.1)
 *
 * Where `vwap.js` resets at every session boundary, AVWAP anchors at a
 * SPECIFIC bar and runs cumulatively from there. Pro traders use AVWAPs
 * from structural points (prior-day high/low, gap bar, session high/low,
 * earnings, news events) as institutional reference levels.
 *
 * Common anchors implemented here:
 *   - prior-day-high     — bar of the highest high in the prior trading day
 *   - prior-day-low      — bar of the lowest  low  in the prior trading day
 *   - prior-day-close    — last bar of the prior trading day
 *   - session-high       — bar of the highest high so far in the current day
 *   - session-low        — bar of the lowest  low  so far in the current day
 *   - custom-time        — caller-provided Unix-sec timestamp
 *
 * Math is the same as standard VWAP:
 *   typicalPrice = (high + low + close) / 3
 *   sumPV    += typicalPrice * volume
 *   sumV     += volume
 *   AVWAP[i]  = sumPV / sumV   (or null if sumV = 0)
 *
 * NaN handling mirrors `vwap.js` / `emas.js`: bars with non-finite OHLC
 * or non-positive volume don't poison the sums and surface as the carry-
 * forward (or null) AVWAP at their index.
 *
 * Pure function. No side effects.
 */

import { getTradingDay } from '../timeService.js';

// ── anchor resolution ────────────────────────────────────────────

/**
 * Find the bar index of the highest high in a given trading-day window.
 * Returns null when no in-day bars exist.
 */
const findHighestHighIdx = (bars, dayStr) => {
  let bestIdx = null;
  let bestHigh = -Infinity;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    const d = getTradingDay(t * 1000);
    if (d !== dayStr) continue;
    const h = Number(bars[i].high);
    if (!Number.isFinite(h)) continue;
    if (h > bestHigh) {
      bestHigh = h;
      bestIdx = i;
    }
  }
  return bestIdx;
};

const findLowestLowIdx = (bars, dayStr) => {
  let bestIdx = null;
  let bestLow = Infinity;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    const d = getTradingDay(t * 1000);
    if (d !== dayStr) continue;
    const l = Number(bars[i].low);
    if (!Number.isFinite(l)) continue;
    if (l < bestLow) {
      bestLow = l;
      bestIdx = i;
    }
  }
  return bestIdx;
};

/** Last bar index of a given trading day, or null if none. */
const findLastBarOfDayIdx = (bars, dayStr) => {
  let lastIdx = null;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    const d = getTradingDay(t * 1000);
    if (d === dayStr) lastIdx = i;
  }
  return lastIdx;
};

/** Find the bar at-or-just-before a given Unix-sec timestamp. */
const findBarAtOrBeforeTime = (bars, targetTs) => {
  if (!Number.isFinite(targetTs)) return null;
  let idx = null;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= targetTs) idx = i;
    else break;
  }
  return idx;
};

/** Distinct trading days present in the bars array, in chronological order. */
const distinctDays = (bars) => {
  const seen = new Set();
  const out = [];
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    const d = getTradingDay(t * 1000);
    if (!d || seen.has(d)) continue;
    seen.add(d);
    out.push(d);
  }
  return out;
};

/**
 * Resolve an anchor specifier to a bar index. Returns null when the
 * anchor can't be found (e.g., prior-day-high requested but bars[]
 * contains only one trading day).
 *
 * @param {Array}  bars      Bar series.
 * @param {string} anchorType
 *   One of: 'prior-day-high', 'prior-day-low', 'prior-day-close',
 *           'session-high',   'session-low',   'custom-time'.
 * @param {Object} [opts]
 * @param {number} [opts.targetDay]      Trading day for "session-*" anchors
 *                                       (YYYY-MM-DD). Defaults to last day in bars.
 * @param {number} [opts.anchorTimeSec]  Required for 'custom-time'.
 */
export const resolveAnchorIdx = (bars, anchorType, opts = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!anchorType) return null;

  // For session-* anchors, default to the LAST trading day in the bars.
  // For prior-* anchors, default to the day BEFORE the last.
  const days = distinctDays(bars);
  if (days.length === 0) return null;
  const lastDay = days[days.length - 1];
  const priorDay = days.length >= 2 ? days[days.length - 2] : null;
  const sessionDay = opts.targetDay || lastDay;
  const priorTarget = opts.targetDay
    ? (days[days.indexOf(opts.targetDay) - 1] || null)
    : priorDay;

  switch (anchorType) {
    case 'prior-day-high':
      return priorTarget ? findHighestHighIdx(bars, priorTarget) : null;
    case 'prior-day-low':
      return priorTarget ? findLowestLowIdx(bars, priorTarget) : null;
    case 'prior-day-close':
      return priorTarget ? findLastBarOfDayIdx(bars, priorTarget) : null;
    case 'session-high':
      return findHighestHighIdx(bars, sessionDay);
    case 'session-low':
      return findLowestLowIdx(bars, sessionDay);
    case 'custom-time':
      return findBarAtOrBeforeTime(bars, Number(opts.anchorTimeSec));
    default:
      return null;
  }
};

// ── AVWAP series ────────────────────────────────────────────────

/**
 * Compute the AVWAP series anchored at `anchorIdx`. The series has the
 * same length as `bars`; values before the anchor are null.
 *
 * @returns {Array<{time:number, value:number|null}>}
 */
export const computeAVWAP = (bars, anchorIdx) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  if (!Number.isFinite(anchorIdx) || anchorIdx < 0 || anchorIdx >= bars.length) {
    return bars.map(b => ({ time: Number(b?.time), value: null }));
  }

  const out = new Array(bars.length);
  let sumPV = 0;
  let sumV = 0;

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const t = Number(b?.time);

    if (i < anchorIdx) {
      out[i] = { time: t, value: null };
      continue;
    }

    const hi = Number(b?.high);
    const lo = Number(b?.low);
    const cl = Number(b?.close);
    const vol = Number(b?.volume);

    if (!Number.isFinite(hi) || !Number.isFinite(lo) || !Number.isFinite(cl) || !Number.isFinite(vol) || vol <= 0) {
      out[i] = { time: t, value: sumV > 0 ? sumPV / sumV : null };
      continue;
    }

    const typicalPrice = (hi + lo + cl) / 3;
    sumPV += typicalPrice * vol;
    sumV  += vol;
    out[i] = { time: t, value: sumV > 0 ? sumPV / sumV : null };
  }

  return out;
};

/**
 * Compute a pack of common AVWAPs in one pass. Returns a map keyed by
 * anchor type with each value containing the resolved anchor index, the
 * anchor bar's time, and the full AVWAP series.
 *
 * Skips anchors that couldn't be resolved (e.g., 'prior-day-high' when
 * bars contain only one trading day).
 *
 * @param {Array} bars
 * @param {Object} [opts]
 * @param {string[]} [opts.anchors]   Subset of supported anchors to compute.
 *                                    Defaults to all standard anchors.
 * @returns {Object} { [anchorType]: { anchorIdx, anchorTime, series } }
 */
export const DEFAULT_AVWAP_ANCHORS = Object.freeze([
  'prior-day-high',
  'prior-day-low',
  'prior-day-close',
  'session-high',
  'session-low',
]);

export const computeAvwapPack = (bars, opts = {}) => {
  const anchors = Array.isArray(opts.anchors) && opts.anchors.length > 0
    ? opts.anchors
    : DEFAULT_AVWAP_ANCHORS;

  const out = {};
  for (const anchorType of anchors) {
    const anchorIdx = resolveAnchorIdx(bars, anchorType, opts);
    if (anchorIdx == null) continue;
    const anchorBar = bars[anchorIdx];
    const anchorTime = Number(anchorBar?.time);
    out[anchorType] = {
      anchorIdx,
      anchorTime: Number.isFinite(anchorTime) ? anchorTime : null,
      series: computeAVWAP(bars, anchorIdx),
    };
  }
  return out;
};

// ── point lookup ────────────────────────────────────────────────

/**
 * Get the AVWAP value at (or just before) a target timestamp. Mirrors
 * `vwapAtTime` in shape so the bar-analysis pipeline can use it the same
 * way.
 */
export const avwapAtTime = (series, targetTime) => {
  if (!Array.isArray(series) || series.length === 0) return null;
  const t = Number(targetTime);
  if (!Number.isFinite(t)) return null;
  let last = null;
  for (let i = 0; i < series.length; i++) {
    const p = series[i];
    if (!p || !Number.isFinite(p.time)) continue;
    if (p.time <= t) {
      if (p.value != null) last = p.value;
    } else {
      break;
    }
  }
  return last;
};

/**
 * Snapshot a pack of AVWAPs at a target timestamp, returning a flat map
 * of anchor → value. Anchors with null values at the target time are
 * omitted.
 */
export const avwapSnapshotAtTime = (pack, targetTime) => {
  if (!pack || typeof pack !== 'object') return {};
  const out = {};
  for (const [key, entry] of Object.entries(pack)) {
    const v = avwapAtTime(entry?.series, targetTime);
    if (v != null) out[key] = v;
  }
  return out;
};
